import React, { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import { Box, Typography, Form, Loader, StatusBanner, useSubscribe } from '@lucid/lego';
import type { FormRef } from '@lucid/lego';
import { BusinessNameSearch } from '../../../../../components/BusinessNameSearch/BusinessNameSearch';
import type {
  OrderDetailsResponse,
  BusinessSearchResult,
} from '../../../../../api/services/lupp/order/dto.order';
import { useUpdateFinanceProviderDetails } from '../../../../../api/services/lupp/order/order';
import { useGetLenders } from '../../../../../api/services/lupp/quotes/quotes';
import type { Lender } from '../../../../../api/services/lupp/quotes/dto.quotes';
import { useStore } from '../../../../../store/store';
import { getCountryCodeFromLocale } from '../../../../../utils/tools';
import { createFinancingFormFields } from './formFields';
import {
  getFinanceTypeFromPaymentMethod,
  isEmbeddedSubventionMethod,
  NOT_APPLICABLE_FALLBACK,
} from './constants';
import PaymentHistory from './PaymentHistory/PaymentHistory';
import FinanceProviderDetails from './FinanceProviderDetails/FinanceProviderDetails';
import InvoiceSummary from './InvoiceSummary/InvoiceSummary';
import type { PaymentHistoryRecord, FinanceProviderDetailsData } from './types';
import styles from './PaymentFinancing.module.scss';

/** Auto-clears success messages after this many milliseconds */
const SUCCESS_MESSAGE_DISPLAY_MS = 3000;

/** Type definition for financing form data */
type FinancingFormData = {
  lienholderName: string;
  financingType: string;
  financingSubtype: string;
  applicationStatus: string;
  applicationDecision: string;
  partnerAppId: string;
  downpayment?: string;
  amountFinanced?: string;
};

/** Stable custom components configuration to prevent unnecessary re-renders */
const customComponents = {
  BusinessNameSearch: BusinessNameSearch as React.ComponentType<Record<string, unknown>>,
};

type PaymentFinancingProps = {
  orderData: OrderDetailsResponse;
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  dataTestId?: string;
};

const PaymentFinancing: React.FC<PaymentFinancingProps> = ({
  orderData,
  theme,
  labels,
  dataTestId = 'payment-financing',
}) => {
  const formRef = useRef<FormRef>(null);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const locale = useStore((state) => state.locale);
  const [isPending, setIsPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const updateFinanceProviderDetails = useUpdateFinanceProviderDetails();

  /** Clean up timeout on unmount to prevent memory leaks */
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  /** Show a success message, then auto-clear it after SUCCESS_MESSAGE_DISPLAY_MS */
  const showSuccessMessage = useCallback((message: string) => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }
    setSuccessMessage(message);
    successTimeoutRef.current = setTimeout(() => {
      setSuccessMessage(null);
      successTimeoutRef.current = null;
    }, SUCCESS_MESSAGE_DISPLAY_MS);
  }, []);

  /** Handle BusinessNameSearch selection for lienholder */
  const handleLienholderChange = useCallback((business: BusinessSearchResult | null) => {
    const setValue = formRef.current?.setValue;
    if (setValue) {
      setValue('lienholderName', business ? business.name : '', {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, []);

  // Memoize default values to prevent unnecessary re-renders
  const financingDefaultValues = useMemo(
    () => ({
      lienholderName:
        orderData.financeProvider ?? orderData.financingDetails?.lienholderName ?? undefined,
      // Until the partner saves, fall back to what the quote set on the order.
      financingType:
        orderData.financingDetails?.financingType ??
        (getFinanceTypeFromPaymentMethod(orderData.preferredPaymentMethod) || undefined),
      financingSubtype:
        orderData.financingDetails?.financingSubtype ?? orderData.customerType ?? undefined,
      applicationStatus: orderData.financingDetails?.applicationStatus ?? undefined,
      applicationDecision: orderData.financingDetails?.applicationDecision ?? undefined,
      partnerAppId: orderData.financingDetails?.partnerAppId ?? undefined,
      downpayment: orderData.financingDetails?.downpayment?.toString() ?? undefined,
      amountFinanced: orderData.financingDetails?.amountFinanced?.toString() ?? undefined,
    }),
    [
      orderData.financeProvider,
      orderData.financingDetails?.lienholderName,
      orderData.financingDetails?.financingType,
      orderData.preferredPaymentMethod,
      orderData.financingDetails?.financingSubtype,
      orderData.customerType,
      orderData.financingDetails?.applicationStatus,
      orderData.financingDetails?.applicationDecision,
      orderData.financingDetails?.partnerAppId,
      orderData.financingDetails?.downpayment,
      orderData.financingDetails?.amountFinanced,
    ]
  );

  // Check if form should be disabled based on applicationSource
  const isFormDisabled = useMemo(() => {
    const applicationSource = orderData.financingDetails?.applicationSource;
    if (!applicationSource) return false;

    const lowerSource = applicationSource.trim().toLowerCase();
    return lowerSource !== 'partner portal';
  }, [orderData.financingDetails?.applicationSource]);

  const savedProvider = financingDefaultValues.lienholderName;
  const [selectedProvider, setSelectedProvider] = useState(savedProvider);

  const {
    data: lenders = [],
    isLoading: isLoadingLenders,
    refetch: refetchLenders,
  } = useGetLenders({
    financeType: getFinanceTypeFromPaymentMethod(orderData.preferredPaymentMethod),
    customerType: orderData.customerType ?? undefined,
  });

  const orderDunsNumber = orderData.financeProviderDetails?.companyIdentifiers?.duns?.dunsNumber;
  // New orders carry the quote's lender only as company details, so match it by DUNS.
  const defaultProvider = useMemo(
    () =>
      savedProvider ??
      lenders.find((lender) => lender.duns != null && String(lender.duns) === orderDunsNumber)
        ?.lenderName,
    [savedProvider, lenders, orderDunsNumber]
  );

  const formDefaultValues = useMemo(
    () => ({ ...financingDefaultValues, lienholderName: defaultProvider }),
    [financingDefaultValues, defaultProvider]
  );

  useEffect(() => {
    setSelectedProvider(defaultProvider);
  }, [defaultProvider]);

  useSubscribe(
    formRef,
    (values, { name }) => {
      if (name === 'lienholderName') {
        setSelectedProvider(values.lienholderName || undefined);
      }
    },
    []
  );

  const lienholderOptions = useMemo(() => {
    if (lenders.length === 0) return undefined;
    const options = lenders.map(({ lenderName }) => ({ label: lenderName, value: lenderName }));
    // Legacy orders hold free-text D&B names; keep them selectable so Save isn't blocked.
    return savedProvider && !options.some((option) => option.value === savedProvider)
      ? [...options, { label: savedProvider, value: savedProvider }]
      : options;
  }, [lenders, savedProvider]);

  const resolveSubventionMethod = useCallback(
    (provider: string | undefined, lenderList: Lender[]) =>
      lenderList.find((lender) => lender.lenderName === provider)?.subventionMethod ??
      (provider === savedProvider ? orderData.subventionMethod : undefined),
    [savedProvider, orderData.subventionMethod]
  );

  const isEmbedded = isEmbeddedSubventionMethod(resolveSubventionMethod(selectedProvider, lenders));
  const notApplicableMessage = labels?.['financeProvider.notApplicable'] ?? NOT_APPLICABLE_FALLBACK;

  // Empty the disabled input so its placeholder shows; restore the saved amount when switching back.
  useEffect(() => {
    formRef.current?.setValue(
      'amountFinanced',
      isEmbedded ? '' : (financingDefaultValues.amountFinanced ?? ''),
      { shouldValidate: false, shouldDirty: false }
    );
  }, [isEmbedded, financingDefaultValues.amountFinanced]);

  /**
   * Seed React Hook Form's lienholderName value on mount so that the form
   * considers the field valid when a defaultValue is pre-populated.
   * Without this, RHF never sees the value set by the BusinessNameSearch
   * defaultValue prop and fires a required-validation error on submit.
   */
  useEffect(() => {
    // The form only mounts once lenders finish loading, so re-seed then.
    if (isLoadingLenders || !defaultProvider) return;
    formRef.current?.setValue('lienholderName', defaultProvider, {
      shouldValidate: false,
      shouldDirty: false,
    });
  }, [defaultProvider, isLoadingLenders]);

  const financingFormFields = useMemo(
    () =>
      createFinancingFormFields(
        labels,
        formDefaultValues,
        theme,
        handleLienholderChange,
        isFormDisabled,
        { lienholderOptions, isEmbedded }
      ),
    [
      labels,
      formDefaultValues,
      theme,
      handleLienholderChange,
      isFormDisabled,
      lienholderOptions,
      isEmbedded,
    ]
  );

  const submitFinancing = useCallback(
    (formData: FinancingFormData, lenderList: Lender[]) => {
      const lender = lenderList.find(({ lenderName }) => lenderName === formData.lienholderName);
      const subventionMethod = resolveSubventionMethod(formData.lienholderName, lenderList);
      // The hidden Amount Financed input keeps its old value in form state; don't send it.
      const amountFinanced = isEmbeddedSubventionMethod(subventionMethod)
        ? undefined
        : formData.amountFinanced;
      const payload = {
        ...formData,
        downpayment: formData.downpayment ? Number(formData.downpayment) : undefined,
        amountFinanced: amountFinanced ? Number(amountFinanced) : undefined,
        // Order GET reads the provider from here, so keep it in sync with the lienholder.
        financeProvider: formData.lienholderName,
        locale: getCountryCodeFromLocale(locale),
        ...(lender && {
          subventionMethod,
          dunsNumber: lender.duns == null ? undefined : String(lender.duns),
        }),
      };

      updateFinanceProviderDetails.mutate(
        {
          orderIdentifier: orderData.orderIdentifier,
          data: {
            paymentFinance: {
              financeProviderDetails: null,
              financingDetails: payload,
            },
          },
        },
        {
          onSuccess: () => {
            setIsPending(false);
            showSuccessMessage(labels?.['details.successMessage'] ?? 'Success');
          },
          onError: () => {
            setIsPending(false);
            setErrorMessage(labels?.['validation.error'] ?? 'Validation error');
          },
        }
      );
    },
    [
      labels,
      showSuccessMessage,
      updateFinanceProviderDetails,
      orderData.orderIdentifier,
      resolveSubventionMethod,
      locale,
    ]
  );

  const handleSuccess = useCallback(
    (data: unknown) => {
      setErrorMessage(null);
      setIsPending(true);
      const formData = data as FinancingFormData;

      if (!lienholderOptions) {
        submitFinancing(formData, lenders);
        return;
      }

      // Re-read FinOps for a current subvention method; never block the save on it.
      refetchLenders()
        .then(({ data: freshLenders }) => freshLenders ?? lenders)
        .catch(() => lenders)
        .then((lenderList) => submitFinancing(formData, lenderList));
    },
    [lienholderOptions, lenders, refetchLenders, submitFinancing]
  );

  const handleError = useCallback(() => {
    setErrorMessage(labels?.['validation.error'] ?? 'Validation error');
    setSuccessMessage(null);
  }, [labels]);

  const handleFinanceProviderSave = useCallback(
    (data: FinanceProviderDetailsData) => {
      setErrorMessage(null);
      setIsPending(true);

      updateFinanceProviderDetails.mutate(
        {
          orderIdentifier: orderData.orderIdentifier,
          data: {
            paymentFinance: {
              financeProviderDetails: {
                name: data.name || '',
                address: data.address || '',
                city: data.city || '',
                postalCode: data.postalCode || '',
                vatNumber: data.vat || '',
              },
              financingDetails: null,
            },
          },
        },
        {
          onSuccess: () => {
            setIsPending(false);
            showSuccessMessage(
              labels['financeProvider.save.success'] || 'Finance provider saved successfully'
            );
          },
          onError: () => {
            setIsPending(false);
            setErrorMessage(
              labels['financeProvider.save.error'] || 'Failed to save finance provider'
            );
          },
        }
      );
    },
    [labels, showSuccessMessage, updateFinanceProviderDetails, orderData.orderIdentifier]
  );

  // Transform payment details from API to PaymentHistoryRecord format
  const paymentHistoryData: PaymentHistoryRecord[] = useMemo(() => {
    if (!orderData.paymentDetails) return [];

    return orderData.paymentDetails.map((payment) => ({
      paymentNumber: payment.referenceId || '',
      paymentDate: payment.paymentDate || '',
      customerOrderAmount: payment.amount || 0,
      paymentType: (payment.transactionType as 'Payment' | 'Refund') || 'Payment',
      paymentStatus: (payment.status as 'Succeeded' | 'Failed' | 'Pending') || 'Pending',
    }));
  }, [orderData.paymentDetails]);

  // Transform finance provider details from API to FinanceProviderDetailsData format
  const financeProviderData: FinanceProviderDetailsData | undefined = useMemo(() => {
    const financeProvider = orderData.financeProviderDetails;
    if (!financeProvider) return undefined;

    // Get the primary address from the addresses array, or fall back to the first address
    const primaryAddress =
      financeProvider.addresses?.find((addr) => addr.type === 'primary') ??
      financeProvider.addresses?.[0];

    return {
      name: financeProvider.companyName ?? undefined,
      address: primaryAddress?.street ?? undefined,
      city: primaryAddress?.city ?? undefined,
      postalCode: primaryAddress?.postalCode ?? undefined,
      vat: financeProvider.companyIdentifiers?.taxId ?? undefined,
    };
  }, [orderData.financeProviderDetails]);

  return (
    <Box data-testid={dataTestId}>
      {/* Loading Overlay */}
      {isPending && (
        <Box className="loadingOverlay" data-testid="loading-overlay">
          <Loader size={32} theme={theme} />
        </Box>
      )}

      {isEmbedded ? (
        <>
          <Box mb={16}>
            <Typography variant="body" size="large" theme={theme} color="black">
              {labels?.['invoice.summary'] ?? ''}
            </Typography>
          </Box>
          <StatusBanner
            type="info"
            message={notApplicableMessage}
            dataTestId="invoice-summary-not-applicable"
          />
        </>
      ) : (
        <InvoiceSummary
          invoiceSummaryDetails={orderData.invoiceSummaryDetails}
          orderData={orderData}
          theme={theme}
          labels={labels}
        />
      )}
      <Box mt={32}>
        {isEmbedded ? (
          <>
            <Box mb={16}>
              <Typography variant="body" size="large" theme={theme} color="black">
                {labels?.['form.paymentHistory'] ?? ''}
              </Typography>
            </Box>
            <StatusBanner
              type="info"
              message={notApplicableMessage}
              dataTestId="payment-history-not-applicable"
            />
          </>
        ) : (
          <PaymentHistory theme={theme} data={paymentHistoryData} labels={labels} locale={locale} />
        )}
      </Box>

      <Box mt={32}>
        {/* Messages */}
        {successMessage && (
          <Box
            mb={16}
            pt={12}
            pb={12}
            pl={16}
            pr={16}
            className={styles.successMessageText}
            data-testid={`${dataTestId}-success-message`}
          >
            <Typography variant="body" size="medium" color="white" theme={theme}>
              {successMessage}
            </Typography>
          </Box>
        )}
        {errorMessage && (
          <Box
            mb={16}
            pt={12}
            pb={12}
            pl={16}
            pr={16}
            className={styles.errorMessageText}
            data-testid={`${dataTestId}-error-message`}
          >
            <Typography variant="body" size="medium" color="white" theme={theme}>
              {errorMessage}
            </Typography>
          </Box>
        )}
        {orderData.preferredPaymentMethod === '3rd Party Financing' && (
          <FinanceProviderDetails
            theme={theme}
            labels={labels}
            data={financeProviderData}
            onSave={handleFinanceProviderSave}
            moduleFileReferenceDtoList={orderData.moduleFileReferenceDtoList}
            sfdcDeliveryIdentifier={orderData.sfdcDeliveryIdentifier}
          />
        )}
        {orderData.preferredPaymentMethod?.startsWith('Lucid Financing') &&
          (isLoadingLenders ? (
            <Loader size={32} theme={theme} />
          ) : (
            <Form
              id="financing-form"
              ref={formRef}
              theme={theme === 'dark' ? 'light' : 'dark'}
              formFields={financingFormFields}
              onSuccess={handleSuccess}
              onError={handleError}
              isFluid={true}
              customComponents={customComponents}
            />
          ))}
      </Box>
    </Box>
  );
};

PaymentFinancing.displayName = 'PaymentFinancing';

export default PaymentFinancing;
